import React from "react";
import { commitMutation, graphql } from "react-relay";

import { faChevronRight } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { confirm } from "~/lib/prompts";
import recaptcha from "~/lib/recaptcha";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import { environment } from "~/models";
import { Button } from "~/ui";
import { DropdownItem } from "~/ui/ArrowDropdown";
import { ButtonLink } from "~/ui/legacyFellowIndex";
import toast from "~/ui/toast";

import { NOTIFICATION_PATH_VALUES, type NotificationPathValueType, PATH_TEXT_MAP, type TaskType } from "./types";

import type { TasksDeleteMutation } from "~/__generated__/TasksDeleteMutation.graphql";
import type { TasksSaveMutation, TasksSaveMutation$variables } from "~/__generated__/TasksSaveMutation.graphql";

import styles from "./Tasks.module.css";

const saveTaskMutation = graphql`
	mutation TasksSaveMutation($streamId: ID!, $automationId: ID!, $task: AutomationTaskInput!, $captchaToken: String!) {
		saveAutomationTask(streamId: $streamId, automationId: $automationId, task: $task, captchaToken: $captchaToken) {
			stream {
				id
				automationConfigurations {
					...AutomationsConfigurationsData @relay(mask: false)
				}
			}
		}
	}
`;

export async function saveTask(
	input: Omit<TasksSaveMutation$variables, "captchaToken">,
	name: string,
	callback?: (success: boolean) => void,
) {
	const captchaToken = await recaptcha("automations");

	commitMutation<TasksSaveMutation>(environment, {
		mutation: saveTaskMutation,
		variables: { captchaToken, ...input },
		onCompleted: ({ saveAutomationTask }, err) => {
			const failed = err?.length || !saveAutomationTask;
			if (failed) {
				toast.error(err?.[0]?.message || "Failed to save automation");
			} else {
				dispatchAnalyticsTrack("Automation Created", { automation: name });
			}

			callback?.(!failed);
		},
	});
}

const deleteTaskMutation = graphql`
	mutation TasksDeleteMutation($taskId: ID!) {
		deleteAutomationTask(taskId: $taskId) {
			configuration {
				...AutomationsConfigurationsData @relay(mask: false)
			}
		}
	}
`;

export async function deleteTask(taskId: string, name: string, callback?: () => void) {
	commitMutation<TasksDeleteMutation>(environment, {
		mutation: deleteTaskMutation,
		variables: { taskId },
		onCompleted: ({ deleteAutomationTask }, err) => {
			if (err?.length || !deleteAutomationTask) {
				toast.error(err?.[0]?.message || `Failed to delete this ${name}`);
			} else {
				callback?.();
			}
		},
	});
}

export const getTimeText = (delta: number) => {
	const adverb = delta > 0 ? "after" : "before";

	const minutes = Math.abs(delta);
	if (minutes < 60) return `${minutes} minutes ${adverb}`;

	const hours = minutes / 60;
	if (hours === 1) return `${hours} hour ${adverb}`;
	if (hours < 24) return `${hours} hours ${adverb}`;

	const days = hours / 24;
	if (days === 1) return `${days} day ${adverb}`;

	return `${days} days ${adverb}`;
};

interface HeaderProps {
	name: string;
	backName?: string;
	input?: Omit<TasksSaveMutation$variables, "captchaToken">;
	showButtons?: boolean;
	onHome: () => void;
	onBack?: () => void;
	onCustomSave?: () => void;
}

export function Header({ name, backName, onHome, onBack, input, showButtons, onCustomSave }: HeaderProps) {
	const [loading, setLoading] = React.useState<boolean>(false);

	const onComplete = React.useCallback(
		(success: boolean) => {
			setLoading(false);

			if (success) {
				onBack?.();
			}
		},
		[onBack],
	);

	const onSave = React.useCallback(() => {
		if (!input || loading) return;

		setLoading(true);
		saveTask(input, name, onComplete);
	}, [input, onComplete, loading, name]);

	return (
		<header className={styles.header}>
			<ButtonLink onClick={onHome} className={styles.link}>
				Automations
			</ButtonLink>
			<FontAwesomeIcon icon={faChevronRight} className={styles.icon} />
			{backName && (
				<>
					<ButtonLink onClick={onBack} className={styles.link}>
						{backName}
					</ButtonLink>
					<FontAwesomeIcon icon={faChevronRight} className={styles.icon} />
				</>
			)}
			<p className={styles.title}>{name}</p>

			{showButtons && (
				<>
					<Button size="normal" variant="secondary" onClick={onBack} className={styles.button}>
						Cancel
					</Button>
					<Button
						size="normal"
						variant="primary"
						onClick={onCustomSave ?? onSave}
						className={styles.button}
						disabled={loading || !(input || onCustomSave)}
					>
						Save
					</Button>
				</>
			)}
		</header>
	);
}

interface DeleteTaskButtonProps {
	taskId: string;
	name: string;
	callback: () => void;
}

export function DeleteTaskButton({ taskId, name, callback }: DeleteTaskButtonProps) {
	const onDelete = React.useCallback(async () => {
		const result = await confirm(`Are you sure you want to delete this ${name}?`, {
			yesProps: { variant: "destructive" },
			title: `Delete ${name}`,
		});

		if (typeof result !== "boolean" || !result) {
			return;
		}

		deleteTask(taskId, name, callback);
	}, [taskId, name, callback]);

	return (
		<Button size="normal" variant="destructive" onClick={onDelete} className={styles.deleteTaskButton}>
			Delete {name}
		</Button>
	);
}

interface ValueDropdownItemProps<T> {
	value: T;
	disabledHelpText?: string;
	onClick: (v: T) => void;
}

export function ValueDropdownItem<T>({
	value,
	disabledHelpText,
	onClick: _onClick,
	children,
}: React.PropsWithChildren<ValueDropdownItemProps<T>>) {
	const onClick = React.useCallback(() => _onClick(value), [value, _onClick]);
	return (
		<DropdownItem onClick={onClick} disabled={!!disabledHelpText} disabledHelpText={disabledHelpText}>
			{children}
		</DropdownItem>
	);
}

interface TaskSummaryProps {
	task: TaskType;
	onClick: (v: string) => void;
}

export function TaskSummary({ task, onClick: _onClick }: TaskSummaryProps) {
	const time = getTimeText(task.delta);
	const pathValue = task.data?.notificationPath as NotificationPathValueType;
	const path = PATH_TEXT_MAP[pathValue];
	const onClick = React.useCallback(() => _onClick(task.id), [_onClick, task.id]);

	let target = "…";
	if (pathValue === NOTIFICATION_PATH_VALUES[0] && task.data?.userIds) {
		const numBillableUsers = task.data.userIds.length;
		target = task.data?.allParticipants
			? "all participants"
			: `${numBillableUsers} ${numBillableUsers === 1 ? "person" : "people"}`;
	} else if (pathValue === NOTIFICATION_PATH_VALUES[0] && task.data?.emails) {
		const numBillableUsers = task.data.emails.length;
		target = task.data?.allParticipants
			? "all participants"
			: `${numBillableUsers} ${numBillableUsers === 1 ? "person" : "people"}`;
	} else if (pathValue === NOTIFICATION_PATH_VALUES[1] && task.data?.channelName) {
		target = task.data.channelName;
	} else if (pathValue === NOTIFICATION_PATH_VALUES[2] && task.data?.teamName && task.data?.conversationName) {
		target = `${task.data.conversationName} (${task.data.teamName})`;
	} else if (task.__typename === "PromptNotificationAutomationTask") {
		target = "required participants";
	}

	return (
		<button className={styles.summaryButton} onClick={onClick}>
			<b>{time}</b>{" "}
			{path && (
				<>
					by <b>{path}</b>
				</>
			)}{" "}
			to <b>{target}</b>
			<span className={styles.whiteSpace} />
			<FontAwesomeIcon icon={faChevronRight} className={styles.icon} />
		</button>
	);
}
